/**
 * Whether the signed-in role holds a capability, for DISPLAY decisions only — greying out a
 * control, choosing a notice. The API is the enforcement point and re-checks every call, so
 * this never grants anything; it only stops a button appearing live when the server would
 * refuse it. Reads the shared permission matrix (no server round trip).
 */

import { useSession } from "@/lib/auth-client";
import { type Capability, PERMISSION_MATRIX } from "@/lib/permissions";

const ROLES = ["ADMIN", "RM", "SA", "ACCT", "FIN"] as const;
type Role = (typeof ROLES)[number];

function isRole(value: unknown): value is Role {
  return ROLES.some((r) => r === value);
}

/** Pure form, so it can be unit-tested without a session. */
export function roleCan(role: unknown, capability: Capability): boolean {
  if (!isRole(role)) return false;
  const scope = PERMISSION_MATRIX[role][capability];
  return scope !== undefined && scope.kind !== "denied";
}

export function useCan(capability: Capability): boolean {
  const { data: session } = useSession();
  return roleCan(session?.user?.role, capability);
}
